"use client";

// useInbox — the data half of the netti inbox.
//
//   const inbox = useInbox(enabled);  // GET /api/you/inbox?limit=  (+ ?before= for older)
//   inbox.markSeen(id) · inbox.unveil(id) · inbox.dismiss(id) · inbox.react(id, r)
//                                     // POST /api/you/inbox  (lib/inbox/types)
//
// ONE store for the whole page: the activity tab's count and the list read the
// same value, so a row turning seen moves the number beside the tab in the same
// frame. The useRepo conventions (components/repo/useRepo): one request per url
// in flight, 401 → "auth", 404 → "missing", stale-while-revalidate against
// FRESH_MS. The store is module scope, so it is written ONLY from effects,
// async callbacks and presses, and the server snapshot is always empty: a
// server render can never hand one person's inbox to the next request.
//
// Live: while anything is subscribed, an INSERT on the person's activity_events
// (a fold landing, an import, a sync) or on their netti_inbox (the pattern
// pass, which writes nothing else) nudges a quiet refetch of the head, on
// channels of its own (the river listens on `activity-feed-<uid>`). The
// netti_inbox insert reaches here only once that table is in the
// supabase_realtime publication. With no signed-in user (the /mirror demo)
// there is simply no channel.
//
// Forget: a record forgotten in its home (the private view sends
// RECORD_FORGOTTEN_EVENT) leaves the store at once, with every arrival that
// points at it, whether or not the list is on screen.
//
// Privacy: the words of a private arrival are never in the store until the
// person asks for them (unveil). Nothing here logs an item, its words or an id.

import { useCallback, useEffect, useSyncExternalStore } from "react";
import { createClient } from "../../lib/supabase/client";
import type { InboxAction as InboxPost, InboxActionResponse, InboxPayload, InboxRingPayload } from "../../lib/inbox/types";
import { askHeadline, type RingAsks } from "../../lib/self/ringView";
import { RING_CHANGED_EVENT, ringChanged } from "../../lib/self/ringEvents";
import { RECORD_FORGOTTEN_EVENT, type RecordForgotten } from "../../lib/you/record";
import { EMPTY, reduce, type AskDecision, type AskHow, type InboxAction, type InboxError, type InboxState, type Reaction } from "./state";

/** a refresh underneath the cached answer, never more often than this */
export const FRESH_MS = 30_000;
/** arrivals per read */
export const PAGE = 30;
/** seen ids wait this long for company before they are told, in one post */
const SEEN_FLUSH_MS = 700;
/** a steady scroll keeps turning rows seen: the batch never waits past this */
const SEEN_MAX_WAIT_MS = 3_000;
/** the most ids the server takes in one post (lib/inbox/shape SEEN_MAX) */
const SEEN_CHUNK = 200;
/** a batch that didn't land is tried again this many times, waiting longer each time */
const SEEN_RETRIES = 4;
/** a burst of activity (a sync writing many rows) refetches once */
const NUDGE_MS = 600;

const URL_BASE = "/api/you/inbox";

// ── the store ────────────────────────────────────────────────────────────────

let state: InboxState = EMPTY;
const listeners = new Set<() => void>();

function dispatch(a: InboxAction): void {
  const next = reduce(state, a);
  if (next === state) return;
  state = next;
  listeners.forEach((l) => l());
}
function subscribe(l: () => void): () => void {
  listeners.add(l);
  return () => {
    listeners.delete(l);
  };
}
const getSnapshot = () => state;
const getServerSnapshot = () => EMPTY;

// ── reads ────────────────────────────────────────────────────────────────────

type Result<T> = { ok: true; data: T } | { ok: false; error: InboxError };
const inflight = new Map<string, Promise<Result<unknown>>>();

/** one request per url in flight; `not` is a read the caller will not share
 *  (it began too early), so a new one starts in its place */
async function getJSON<T>(url: string, not?: Promise<Result<unknown>>): Promise<Result<T>> {
  let p = inflight.get(url) as Promise<Result<T>> | undefined;
  if (p && p === not) p = undefined;
  if (!p) {
    const own: Promise<Result<T>> = (async (): Promise<Result<T>> => {
      try {
        const res = await fetch(url, { cache: "no-store", credentials: "same-origin" });
        if (res.status === 401) return { ok: false, error: "auth" };
        if (res.status === 404 || res.status === 403) return { ok: false, error: "missing" };
        if (!res.ok) return { ok: false, error: "failed" };
        return { ok: true, data: (await res.json()) as T };
      } catch {
        return { ok: false, error: "failed" };
      } finally {
        // only this read's own entry: a newer read may hold the url by now
        window.setTimeout(() => {
          if (inflight.get(url) === own) inflight.delete(url);
        }, 0);
      }
    })();
    p = own;
    inflight.set(url, p as Promise<Result<unknown>>);
  }
  return p;
}

function isPayload(d: unknown): d is InboxPayload {
  return !!d && typeof d === "object" && Array.isArray((d as InboxPayload).items);
}

/** read the head. `force` skips the freshness check (a nudge, a try again),
 *  and never settles for a read that began before it was asked: that one
 *  lands first, then a new one, so the newer answer is the one that stays. */
async function refresh(force: boolean): Promise<void> {
  if (!force && state.loaded && Date.now() - state.fetchedAt < FRESH_MS) return;
  const url = `${URL_BASE}?limit=${PAGE}`;
  const early = force ? inflight.get(url) : undefined;
  if (early) await early;
  const r = await getJSON<InboxPayload>(url, early);
  if (r.ok && isPayload(r.data)) {
    dispatch({ type: "head", page: r.data, at: Date.now() });
    // the head carries the ring's asks too: nothing waiting is said twice
    if (r.data.ring) for (const q of r.data.ring.requests) ringKnown.add(q.id);
  } else dispatch({ type: "error", error: r.ok ? "failed" : r.error });
}

async function loadMore(): Promise<void> {
  const s = state;
  if (!s.loaded || s.paging || !s.nextCursor) return;
  dispatch({ type: "paging", on: true });
  const r = await getJSON<InboxPayload>(`${URL_BASE}?limit=${PAGE}&before=${encodeURIComponent(s.nextCursor)}`);
  if (r.ok && isPayload(r.data)) dispatch({ type: "page", page: r.data });
  else dispatch({ type: "paging", on: false, failed: true });
}

// ── writes ───────────────────────────────────────────────────────────────────

async function post(body: InboxPost): Promise<InboxActionResponse | null> {
  try {
    const res = await fetch(URL_BASE, {
      method: "POST",
      cache: "no-store",
      credentials: "same-origin",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(body),
    });
    if (!res.ok) return null;
    const json = (await res.json().catch(() => null)) as InboxActionResponse | null;
    return json && json.ok ? json : null;
  } catch {
    return null;
  }
}

// seen: counted here at once (the dot fades, the tab's number follows), told
// to the server in one batch a moment later (never more than SEEN_CHUNK ids,
// never later than SEEN_MAX_WAIT_MS). A batch that doesn't land waits for the
// next one, so the dots don't come back on the next visit.
const seenQueue = new Set<string>();
let seenTimer: number | null = null;
/** when the oldest id still waiting was queued (0: none waiting) */
let seenSince = 0;
let seenFails = 0;

function armSeen(ms: number): void {
  if (seenTimer != null) window.clearTimeout(seenTimer);
  seenTimer = window.setTimeout(flushSeen, ms);
}

function flushSeen(): void {
  if (seenTimer != null) window.clearTimeout(seenTimer);
  seenTimer = null;
  if (seenQueue.size === 0) return;
  const ids = [...seenQueue].slice(0, SEEN_CHUNK);
  for (const id of ids) seenQueue.delete(id);
  seenSince = seenQueue.size ? Date.now() : 0;
  if (seenQueue.size) armSeen(0);
  void post({ action: "seen", ids }).then((r) => {
    if (!r) {
      // not told: back in line, a little later each time, unless the server
      // keeps saying no (then these go, and the next batch gets its own tries)
      if (seenFails >= SEEN_RETRIES) {
        seenFails = 0;
        return;
      }
      seenFails++;
      for (const id of ids) seenQueue.add(id);
      if (!seenSince) seenSince = Date.now();
      armSeen(SEEN_FLUSH_MS * 2 ** seenFails);
      return;
    }
    seenFails = 0;
    // the server's count, unless more seen ids are already waiting to be told
    if (typeof r.unseen === "number" && seenQueue.size === 0) dispatch({ type: "unseen", n: r.unseen });
  });
}

function markSeen(id: string): void {
  const it = state.items.find((i) => i.id === id);
  if (!it || it.seen) return;
  dispatch({ type: "seen", ids: [id] });
  seenQueue.add(id);
  const now = Date.now();
  if (!seenSince) seenSince = now;
  if (seenQueue.size >= SEEN_CHUNK) return flushSeen();
  armSeen(Math.min(SEEN_FLUSH_MS, Math.max(0, seenSince + SEEN_MAX_WAIT_MS - now)));
}

/** ask for a private arrival's words. true when they came back. */
async function unveil(id: string): Promise<boolean> {
  if (state.unveiling.includes(id)) return false;
  dispatch({ type: "unveiling", id, on: true });
  const r = await post({ action: "unveil", id });
  if (r?.item && r.item.id === id && !r.item.veiled) {
    dispatch({ type: "item", item: r.item });
    if (typeof r.unseen === "number" && seenQueue.size === 0) dispatch({ type: "unseen", n: r.unseen });
    return true;
  }
  dispatch({ type: "unveiling", id, on: false });
  return false;
}

/** out of the inbox (the record keeps its home). Put back if the server says no. */
async function dismiss(id: string): Promise<boolean> {
  const item = state.items.find((i) => i.id === id);
  dispatch({ type: "dismiss", id });
  const queued = seenQueue.delete(id);
  const r = await post({ action: "dismiss", id });
  if (!r) {
    dispatch({ type: "undismiss", id, item });
    if (queued && state.items.some((i) => i.id === id)) {
      seenQueue.add(id);
      if (!seenSince) seenSince = Date.now();
      armSeen(SEEN_FLUSH_MS);
    }
    void refresh(true);
    return false;
  }
  if (typeof r.unseen === "number" && seenQueue.size === 0) dispatch({ type: "unseen", n: r.unseen });
  return true;
}

/** a pattern's reaction (pressing the one already chosen takes it back). */
async function react(id: string, reaction: Reaction): Promise<boolean> {
  const before = state.items.find((i) => i.id === id)?.reaction ?? null;
  dispatch({ type: "react", id, reaction });
  const r = await post({ action: "react", id, reaction });
  if (!r) {
    dispatch({ type: "react", id, reaction: before });
    return false;
  }
  if (r.item && r.item.id === id) dispatch({ type: "item", item: r.item });
  if (typeof r.unseen === "number" && seenQueue.size === 0) dispatch({ type: "unseen", n: r.unseen });
  return true;
}

// ── the private ring's asks ─────────────────────────────────────────────────
//
// An app's request waits in the inbox as a row of its own (lib/self/ringView):
// answered right there (allow once · always · no) through /api/you/ring, the
// same door the you page uses (self_access_decide, on the person's own session).
//
// How a new request reaches someone (the least that works, no migration): the
// door writes self_access_requests with the service role, and that table is
// not in the realtime publication, so nothing is pushed. Instead a quiet poll
// asks GET /api/you/inbox?ring=1 (the asks alone: no backfill, no records, no
// pattern pass) every RING_POLL_MS while the page is visible, and at once when
// the tab comes back into view. A request the page has not seen before says so
// once in the corner (the Noticer's quiet line), and counts beside the inbox
// tab until it is answered. Founders and testers only (F.privateRing): for
// anyone else the poll is never started, and a 404 stops it for good.

/** how often the quiet poll asks, while the page is visible */
export const RING_POLL_MS = 60_000;
/** a tab coming back into view asks again, but never more often than this */
const RING_WAKE_MS = 15_000;
/** the corner has said "something is waiting" once this session */
const RING_TOLD_KEY = "ib:ring-told";

let ringClosed = false;
let ringReadAt = 0;
/** request ids this page has already been told about */
const ringKnown = new Set<string>();
let ringSeeded = false;

function isRingPayload(d: unknown): d is InboxRingPayload {
  return !!d && typeof d === "object" && !!(d as InboxRingPayload).ring && Array.isArray((d as InboxRingPayload).ring.requests);
}

/** the corner's quiet line (StatusCorner reads `innernet:clip` notes) */
function tell(line: string): void {
  try {
    window.dispatchEvent(new CustomEvent("innernet:clip", { detail: { action: "note", name: line } }));
  } catch {
    /* no corner here */
  }
}

/** say a request aloud once: the first this page sees of it */
function noticeNew(ring: RingAsks | null | undefined): void {
  if (!ring) return;
  const fresh = ring.requests.filter((r) => !ringKnown.has(r.id));
  for (const r of ring.requests) ringKnown.add(r.id);
  if (!fresh.length) {
    ringSeeded = true;
    return;
  }
  let told = false;
  try {
    told = window.sessionStorage.getItem(RING_TOLD_KEY) === "1";
  } catch {
    /* storage can be unavailable */
  }
  // the first read of a visit says so once per session; after that, each new one
  if (!ringSeeded && told) {
    ringSeeded = true;
    return;
  }
  ringSeeded = true;
  const r = fresh[0];
  tell(fresh.length > 1 ? `${fresh.length} apps are waiting on you in your inbox.` : `${askHeadline(r)}. it waits in your inbox.`);
  try {
    window.sessionStorage.setItem(RING_TOLD_KEY, "1");
  } catch {
    /* fine */
  }
}

/** read the ring's asks alone. `force` skips the wake throttle. */
async function refreshRing(force = false): Promise<void> {
  if (ringClosed) return;
  if (!force && Date.now() - ringReadAt < RING_WAKE_MS) return;
  ringReadAt = Date.now();
  const r = await getJSON<InboxRingPayload>(`${URL_BASE}?ring=1`);
  if (!r.ok) {
    if (r.error === "missing" || r.error === "auth") ringClosed = true;
    return;
  }
  if (!isRingPayload(r.data)) return;
  dispatch({ type: "ring", ring: r.data.ring });
  noticeNew(state.ring);
}

/** answer one request, from the inbox. The row turns into what happened at
 *  once; a no from the server puts the request back and says so. */
async function answer(id: string, decision: AskDecision, how?: AskHow): Promise<boolean> {
  if (!state.ring?.requests.some((r) => r.id === id)) return false;
  dispatch({ type: "answer", id, decision, ...(how ? { how } : {}) });
  let ok = false;
  try {
    const res = await fetch("/api/you/ring", {
      method: "POST",
      cache: "no-store",
      credentials: "same-origin",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({
        request_id: id,
        decision,
        ...(how?.length ? { length: how.length } : {}),
        ...(how?.categories ? { categories: how.categories } : {}),
      }),
    });
    // 409: already decided (on the you page, in another tab): it is not waiting any more
    ok = res.ok || res.status === 409;
  } catch {
    ok = false;
  }
  if (!ok) {
    dispatch({ type: "unanswer", id });
    return false;
  }
  dispatch({ type: "answer-landed", id });
  ringChanged("inbox");
  void refreshRing(true);
  return true;
}

function clearAnswer(id: string): void {
  dispatch({ type: "answer-clear", id });
}

// a decision or a setting made on another surface (the you page, the access
// sheet): read the asks again, if this page reads them at all
function onRingChanged(e: Event): void {
  const from = (e as CustomEvent<{ from?: string }>).detail?.from;
  if (from === "inbox" || ringClosed || state.ring == null) return;
  void refreshRing(true);
}
if (typeof window !== "undefined" && typeof window.addEventListener === "function") {
  window.addEventListener(RING_CHANGED_EVENT, onRingChanged);
}

let watchCount = 0;
let watchStop: (() => void) | null = null;

function startWatch(): () => void {
  let timer: number | null = null;
  const tick = () => {
    if (document.visibilityState === "visible") void refreshRing(true);
  };
  const wake = () => {
    if (document.visibilityState === "visible") void refreshRing(false);
  };
  void refreshRing(true);
  timer = window.setInterval(tick, RING_POLL_MS);
  document.addEventListener("visibilitychange", wake);
  window.addEventListener("focus", wake);
  return () => {
    if (timer != null) window.clearInterval(timer);
    document.removeEventListener("visibilitychange", wake);
    window.removeEventListener("focus", wake);
  };
}

/**
 * Keep the ring's asks current while mounted (the dashboard mounts this once,
 * for the people the ring is open to). The inbox tab's count and the inbox's
 * waiting rows read the same store, so a new request shows up in both.
 */
export function useRingWatch(enabled: boolean): void {
  useEffect(() => {
    if (!enabled) return;
    watchCount++;
    if (watchCount === 1) watchStop = startWatch();
    return () => {
      watchCount = Math.max(0, watchCount - 1);
      if (watchCount === 0 && watchStop) {
        watchStop();
        watchStop = null;
      }
    };
  }, [enabled]);
}

// ── forget: the record left its home, so its arrivals leave here ────────────

function onForgotten(e: Event): void {
  const d = (e as CustomEvent<RecordForgotten>).detail;
  if (!d || (d.kind !== "fact" && d.kind !== "pattern") || !Array.isArray(d.ids)) return;
  const ids = d.ids.filter((x): x is string => typeof x === "string" && x.length > 0);
  if (!ids.length) return;
  dispatch({ type: "forgotten", kind: d.kind, ids });
  // the forget has landed (the event is sent after the server said so): while
  // the tab's count is on screen, read the head again so the number follows
  if (liveCount > 0) void refresh(true);
}
if (typeof window !== "undefined" && typeof window.addEventListener === "function") {
  window.addEventListener(RECORD_FORGOTTEN_EVENT, onForgotten);
}

// ── live: one channel while anyone is listening ──────────────────────────────

let liveCount = 0;
let liveStop: (() => void) | null = null;

function startLive(): () => void {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const channels: any[] = [];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let supabase: any = null;
  let timer: ReturnType<typeof setTimeout> | null = null;
  let disposed = false;

  // the payload is never read: an insert only says "read the head again"
  const nudge = () => {
    if (timer) clearTimeout(timer);
    timer = setTimeout(() => void refresh(true), NUDGE_MS);
  };

  (async () => {
    try {
      supabase = createClient();
      const { data } = await supabase.auth.getUser();
      const uid = data?.user?.id;
      if (disposed || !uid) return;
      // a fold, an import, a sync: they write the activity river
      const river = supabase.channel(`activity-inbox-${uid}`);
      river.on("postgres_changes", { event: "INSERT", schema: "public", table: "activity_events", filter: `user_id=eq.${uid}` }, nudge);
      river.subscribe();
      channels.push(river);
      // the pattern pass writes the inbox and nothing else. Its own channel,
      // so a table not yet in the realtime publication can never quiet the river.
      const rows = supabase.channel(`activity-inbox-rows-${uid}`);
      rows.on("postgres_changes", { event: "INSERT", schema: "public", table: "netti_inbox", filter: `user_id=eq.${uid}` }, nudge);
      rows.subscribe();
      channels.push(rows);
    } catch {
      /* no realtime here: the mount read and a return visit keep it current */
    }
  })();

  return () => {
    disposed = true;
    if (timer) clearTimeout(timer);
    for (const channel of channels.splice(0)) {
      try {
        if (supabase) supabase.removeChannel(channel);
        else channel.unsubscribe();
      } catch {
        /* ignore */
      }
    }
  };
}

function retainLive(): () => void {
  liveCount++;
  if (liveCount === 1) liveStop = startLive();
  let released = false;
  return () => {
    if (released) return;
    released = true;
    liveCount = Math.max(0, liveCount - 1);
    if (liveCount === 0 && liveStop) {
      liveStop();
      liveStop = null;
    }
  };
}

// ── the hook ─────────────────────────────────────────────────────────────────

/**
 * The store as it stands, read without asking for anything: the you space's
 * one line of what waits (v1.1) reads the asks the dashboard's RingWatch keeps
 * current, and never pulls the arrivals themselves.
 */
export function useInboxState(): InboxState {
  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
}

export interface UseInbox extends InboxState {
  /** the first read is on its way (nothing to show yet) */
  loading: boolean;
  reload: () => void;
  loadMore: () => void;
  markSeen: (id: string) => void;
  unveil: (id: string) => Promise<boolean>;
  dismiss: (id: string) => Promise<boolean>;
  react: (id: string, reaction: Reaction) => Promise<boolean>;
  /** answer an app's request (the private ring) */
  answer: (id: string, decision: AskDecision, how?: AskHow) => Promise<boolean>;
  /** fold an answered request away */
  clearAnswer: (id: string) => void;
}

/** the netti inbox. `enabled` false (the gate is closed) reads nothing. */
export function useInbox(enabled = true): UseInbox {
  const s = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);

  useEffect(() => {
    if (!enabled) return;
    void refresh(false);
    return retainLive();
  }, [enabled]);

  const reload = useCallback(() => {
    dispatch({ type: "retry" });
    void refresh(true);
  }, []);
  const more = useCallback(() => void loadMore(), []);

  return {
    ...s,
    loading: enabled && !s.loaded && !s.error,
    reload,
    loadMore: more,
    markSeen,
    unveil,
    dismiss,
    react,
    answer,
    clearAnswer,
  };
}

/** the store's moving parts, for its own tests */
export const __test = {
  getState: () => state,
  dispatch,
  refresh,
  markSeen,
  dismiss,
  onForgotten,
  answer,
  refreshRing,
  reset(): void {
    state = EMPTY;
    ringClosed = false;
    ringReadAt = 0;
    ringKnown.clear();
    ringSeeded = false;
    inflight.clear();
    seenQueue.clear();
    if (seenTimer != null) window.clearTimeout(seenTimer);
    seenTimer = null;
    seenSince = 0;
    seenFails = 0;
  },
};
